<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';
import { PlayIcon, ArrowPathIcon, EllipsisHorizontalIcon } from '@heroicons/vue/24/solid';
import { toast } from 'vue-sonner';
import TrackCard from '@/components/TrackCard.vue';
import TrackListExportControls from '@/components/TrackListExportControls.vue';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LazyTooltip } from '@/components/ui/tooltip';
import { TRACK_CARD_DENSITIES, useTrackCardDensity } from '@/composables/trackCardDensity';
import { usePermissions } from '@/composables/usePermissions';
import { useToolbarFit } from '@/composables/useToolbarFit';
import type { PlaylistTrackRow } from '@/composables/api/usePlaylistSwitchingApi';
import type { TrackListExportSource } from '@/types/trackListExport';
import { provideTrackQueueContext, type TrackQueueContext } from '@/composables/trackQueueContext';
import { playTrackListInContext } from '@/composables/playTrackListInContext';
import { buildQueueEntry, useAudioPlaybackStore } from '@/stores/playerStore';

/**
 * Shared TrackCard fleet with loading / error / empty states. Single rendering
 * surface for every "tracks of a list" view (Playlists report Tracks tab, the
 * usage drill-down inspector) so the card list always looks and behaves the same.
 *
 * The density toggle (compact / default / full) drives a shared, persisted
 * preference so every open track list stays in sync; pass `hideDensityToggle`
 * for hosts that render their own control.
 *
 * Pass `exportSource` to offer the spreadsheet export. The controls hide
 * themselves when the user lacks the export permission.
 *
 * The toolbar collapses on room rather than on viewport breakpoints — labels
 * go first, then every action into one overflow menu — because its usual home
 * is the inspector panel, which the user drags to whatever width they like.
 * See `toolbarFit` below.
 */
const props = withDefaults(
  defineProps<{
    tracks: PlaylistTrackRow[];
    isLoading?: boolean;
    hasError?: boolean;
    errorText?: string;
    emptyText?: string;
    hideDensityToggle?: boolean;
    /** Identifies this list to the export endpoint. Omit to hide the export controls. */
    exportSource?: TrackListExportSource | null;
    /** Label for the export toast, e.g. "Bucket - 90s Alt Rock". */
    exportLabel?: string;
    /**
     * The list's true length. Paged hosts must pass it — `tracks` there holds
     * only what has been loaded so far, so counting it would under-report the
     * list until the user pressed "Load more". Hosts that load the whole list
     * omit it and get `tracks.length`.
     */
    totalCount?: number | null;
  }>(),
  {
    hideDensityToggle: false,
    exportSource: null,
    exportLabel: 'Track list',
    totalCount: null,
  },
);

const density = useTrackCardDensity();

const trackCount = computed(() => props.totalCount ?? props.tracks.length);

/**
 * The same source that identifies this list to the export endpoint identifies
 * it to the queue endpoint, so one provide here makes every Play button in this
 * list play-in-context — covering both the playlist-usage and generated-playlist
 * inspectors without touching TrackCard or CoverArtTile.
 *
 * `knownTotal` is the list's real length (`totalCount` for a paged host, not the
 * rows on screen), which is what lets a one-track list skip the queue request
 * altogether instead of asking the server for a list it is already holding.
 */
const queueContext = computed<TrackQueueContext | null>(() =>
  props.exportSource
    ? { source: props.exportSource, label: props.exportLabel, knownTotal: trackCount.value }
    : null,
);

provideTrackQueueContext(() => queueContext.value);

const playback = useAudioPlaybackStore();
const isStartingPlayAll = ref(false);

/**
 * The first row of the list, as the queue entry to start on.
 *
 * "Play all" needs a track to play IMMEDIATELY — the list request takes as long
 * as it takes, and a play gesture that produces no sound until it comes back
 * feels broken. The first loaded row is that track, and it is the right one: a
 * paged host loads from the top, so `tracks[0]` is genuinely track 1 and not
 * merely the first one that happened to arrive.
 */
const playAllSeedEntry = computed(() => {
  const first = props.tracks[0];
  if (!first?.code) {
    return null;
  }

  const name = first.name ?? '';
  const remix = first.remix_name ?? '';

  return buildQueueEntry({
    identity: { kind: 'live_track', code: first.code },
    source: 'live_mp3',
    code: first.code,
    // Composed exactly as TrackCard composes its own title, so the player bar
    // reads the same as the row it started from.
    title: name === '' ? first.code : remix === '' ? name : `${name} (${remix})`,
    artist: first.artist_name,
    album: first.album_name,
    coverUrl: first.cover_art_signed_url,
    position: first.position ?? null,
    // The player bar links its three identity lines off these. Without them the
    // seed carried nulls, and `playListInContext` splices the seed OVER the row
    // it matched in the loaded list — so track 1's title, artist and album were
    // dead text while it played, came alive on track 2, and died again on the
    // way back. Every other Play button already passes them.
    trackEntityId: 'LiveTrack',
    trackRecordId: first.id,
    artistId: first.artist_id ?? null,
    albumId: first.album_id ?? null,
    // And the card, so a one-track list — which skips the request entirely — is
    // still a track card rather than the bare fallback row.
    card: first,
  });
});

/** Offered only where there is a list to queue AND a first track to start it on. */
const canPlayAll = computed(
  () => queueContext.value !== null && !props.isLoading && playAllSeedEntry.value !== null,
);

async function onPlayAll(): Promise<void> {
  const context = queueContext.value;
  const seedEntry = playAllSeedEntry.value;
  if (context === null || seedEntry === null || isStartingPlayAll.value) {
    return;
  }

  isStartingPlayAll.value = true;
  try {
    const loadedList = await playTrackListInContext(playback, seedEntry, context);
    if (!loadedList) {
      toast.error('Could not load the rest of the list.');
    }
  } finally {
    isStartingPlayAll.value = false;
  }
}

/**
 * Room-based toolbar collapse, the same three-state ladder the search toolbar
 * walks (see toolbarFitState.ts): 'full' is a labelled button each, 'icons'
 * drops the labels, 'collapsed' folds every action into one overflow menu.
 *
 * This list's toolbar lives inside the inspector panel, which the user drags
 * as narrow as 300px. Without a ladder the action cluster simply overflowed
 * the panel and got clipped by its `overflow-hidden` — "Export to Excel" and
 * the track count were sliced off the right edge with nothing to say they
 * existed. Room decides, not viewport breakpoints: the window can be a 27"
 * display and the panel still be 300px wide.
 *
 * The row is the room and the cluster is the content, deliberately two
 * different boxes. The row is full-width, so its `clientWidth` is the room
 * whether or not the buttons fit; the cluster is shrink-to-fit, so measuring
 * it against itself would report "fits" the moment it collapsed and the
 * toolbar could never expand again when the panel was widened.
 */
const toolbarRowRef = ref<HTMLElement | null>(null);
const toolbarActionsRef = ref<HTMLElement | null>(null);
const { can } = usePermissions();

/** True when there is at least one action to fold into the overflow menu. */
const hasToolbarActions = computed(
  () => canPlayAll.value || (props.exportSource !== null && can('view', 'list_export')),
);

/**
 * Everything the measured widths depend on besides the box: the count's text
 * ("3 tracks" and "12,481 tracks" are different widths) and which controls are
 * present at all.
 */
const toolbarLabelSignature = computed(() =>
  [String(trackCount.value), String(canPlayAll.value), String(hasToolbarActions.value)].join('|'),
);

const { fitState: toolbarFit } = useToolbarFit({
  roomRef: toolbarRowRef,
  contentRef: toolbarActionsRef,
  labelSignature: toolbarLabelSignature,
});

/**
 * How many cards get mounted at a time.
 *
 * A bucket's Tracks tab fetches 500 tracks per page, and a track card is not a
 * cheap thing to mount: each one carries a cover-art popover, a play-overlay
 * tooltip, rating / vote / tag chips. Mounting five hundred of them in one go
 * blocked the main thread for long enough that the inspector could not be
 * closed while it happened, and left a DOM big enough to make the whole app
 * feel slow afterwards. So the list mounts a screenful at a time and grows as
 * the user scrolls towards the end of what is rendered.
 *
 * This is separate from the "Load more" button, which fetches the NEXT page
 * from the backend. Loading a page and rendering it are now two different
 * things.
 */
const RENDER_CHUNK = 40;
/** Start mounting the next chunk this far before the end scrolls into view. */
const GROW_MARGIN_PX = 800;

const renderLimit = ref(RENDER_CHUNK);
const visibleTracks = computed(() => props.tracks.slice(0, renderLimit.value));
const hasUnrenderedTracks = computed(() => renderLimit.value < props.tracks.length);

/**
 * Generated playlists legitimately repeat a track, so key by id + position:
 * each occurrence is then a distinct node with its own popover state.
 */
function trackKey(track: PlaylistTrackRow, index: number): string {
  return `${track.id}-${track.position ?? index}`;
}

/**
 * "Load more" appends the next page to the same list, and that must not throw
 * away what is already mounted. Anything else — a different list in the same
 * inspector, a refetch that came back shorter — starts the window over.
 */
watch(
  () => props.tracks,
  (nextTracks, previousTracks) => {
    const isSameListExtended =
      previousTracks !== undefined &&
      previousTracks.length > 0 &&
      nextTracks.length >= previousTracks.length &&
      nextTracks[0]?.id === previousTracks[0]?.id;
    if (!isSameListExtended) {
      renderLimit.value = RENDER_CHUNK;
    }
  },
);

const sentinelRef = ref<HTMLElement | null>(null);

/**
 * Mount one chunk per frame for as long as the end of the rendered list is
 * still within reach of the viewport. Re-measuring each frame (rather than
 * waiting for the observer to report back) is what keeps a tall panel from
 * overshooting by several chunks: Vue has already patched the DOM by the time
 * the next frame's callback runs, so the sentinel is measured where it
 * actually ended up.
 */
function growUntilSentinelIsOutOfReach(): void {
  if (!hasUnrenderedTracks.value) {
    return;
  }
  const sentinelRect = sentinelRef.value?.getBoundingClientRect();
  if (!sentinelRect || sentinelRect.top > window.innerHeight + GROW_MARGIN_PX) {
    return;
  }
  renderLimit.value = Math.min(renderLimit.value + RENDER_CHUNK, props.tracks.length);
  requestAnimationFrame(growUntilSentinelIsOutOfReach);
}

useIntersectionObserver(
  sentinelRef,
  ([entry]) => {
    if (entry?.isIntersecting) {
      growUntilSentinelIsOutOfReach();
    }
  },
  { rootMargin: `${GROW_MARGIN_PX}px` },
);

const DENSITY_LABELS: Record<(typeof TRACK_CARD_DENSITIES)[number], string> = {
  compact: 'Compact',
  default: 'Default',
  full: 'Full',
};
</script>

<template>
  <div class="flex flex-col gap-3" data-testid="track-card-list">
    <div
      v-if="hasError"
      class="border-destructive/50 bg-destructive/10 text-destructive rounded-md border p-3 text-sm"
    >
      {{ errorText ?? "Could not load the playlist's tracks." }}
    </div>
    <template v-else>
      <!--
        The toolbar sits OUTSIDE the loading and empty checks on purpose.
        Neither state has anything to do with whether this list can be exported:
        the export posts an identifier (`{ type: 'bucket', bucket_id }`) and the
        backend resolves the tracks itself, so an empty list exports fine as a
        header-only workbook and a list still loading exports fine too.

        Both placements caused a real bug. Inside the non-empty branch, an empty
        list was impossible to export. Below the loading branch, the Tracks tab
        of a bucket or custom list showed NO export button and no density toggle
        for the whole of its first fetch — and after a page refresh the query
        cache is empty, so that fetch is exactly what the user lands on. A
        bucket resolves thousands of tracks in pages of 500, so it is not a
        flicker. That is the "the export button wasn't there after I refreshed"
        report.

        The error branch above is deliberately NOT included: when we have just
        told the user we could not read this list, offering to build a
        spreadsheet of it invites a second failure.
      -->
      <div ref="toolbarRowRef" class="flex flex-wrap items-center justify-between gap-2">
        <div
          v-if="!hideDensityToggle"
          class="bg-muted inline-flex shrink-0 items-center rounded-lg p-[3px] text-xs"
          role="group"
          aria-label="Track card density"
        >
          <button
            v-for="option in TRACK_CARD_DENSITIES"
            :key="option"
            type="button"
            class="cursor-pointer rounded-md px-2.5 py-1 font-medium transition-colors"
            :class="
              density === option
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            "
            :aria-pressed="density === option"
            :data-testid="`track-card-density-${option}`"
            @click="density = option"
          >
            {{ DENSITY_LABELS[option] }}
          </button>
        </div>
        <!-- Keeps the export controls right-aligned when there is no density toggle. -->
        <div v-else />

        <!--
          `min-w-0`: this cluster is what the fit ladder collapses, and until a
          demotion settles it must clip at the row's edge rather than spill out
          of the panel.
        -->
        <div ref="toolbarActionsRef" class="flex min-w-0 items-center gap-2">
          <template v-if="toolbarFit !== 'collapsed'">
            <!--
              Queueing the whole list used to require picking a track first: the
              only play-in-context gesture was clicking one row's cover art, so
              "play this bucket" had no control of its own.

              The aria-label goes on only once the word is hidden: an aria-label
              overrides the visible text, so setting both would leave the
              accessible name disagreeing with what is on screen.
            -->
            <LazyTooltip v-if="canPlayAll" :enabled="toolbarFit !== 'full'">
              <Button
                type="button"
                variant="outline"
                size="sm"
                class="px-2"
                :disabled="isStartingPlayAll"
                :aria-label="toolbarFit === 'full' ? undefined : 'Play all'"
                data-testid="track-card-list-play-all"
                @click="onPlayAll"
              >
                <ArrowPathIcon v-if="isStartingPlayAll" class="size-4 shrink-0 animate-spin" />
                <PlayIcon v-else class="size-4 shrink-0" />
                <span v-if="toolbarFit === 'full'">Play all</span>
              </Button>
              <template #content>Play all</template>
            </LazyTooltip>
            <TrackListExportControls
              v-if="exportSource"
              :source="exportSource"
              :label="exportLabel"
              :fit="toolbarFit"
            />
          </template>

          <!--
            Narrower than an icon row: every action moves into one menu so the
            count still has somewhere to sit. Nothing is dropped — the menu
            holds the same controls, with the same test ids.
          -->
          <!-- The tooltip wraps the whole menu, not just its trigger. See LazyTooltip for why. -->
          <LazyTooltip v-else-if="hasToolbarActions">
            <DropdownMenu>
              <DropdownMenuTrigger as-child>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  class="px-2"
                  aria-label="Track list actions"
                  data-testid="track-card-list-actions-menu"
                >
                  <EllipsisHorizontalIcon class="size-4 shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  v-if="canPlayAll"
                  :disabled="isStartingPlayAll"
                  data-testid="track-card-list-play-all"
                  @click="onPlayAll"
                >
                  <ArrowPathIcon v-if="isStartingPlayAll" class="mr-2 size-4 animate-spin" />
                  <PlayIcon v-else class="mr-2 size-4" />
                  Play all
                </DropdownMenuItem>
                <TrackListExportControls
                  v-if="exportSource"
                  :source="exportSource"
                  :label="exportLabel"
                  fit="collapsed"
                />
              </DropdownMenuContent>
            </DropdownMenu>
            <template #content>Track list actions</template>
          </LazyTooltip>
          <!--
            Deliberately outside the export controls: those hide themselves for
            users without the export permission, and how long the list is is
            everyone's business.

            Held back while the first fetch is in flight. The toolbar now
            renders during loading (so the export button doesn't disappear for
            the whole of a bucket's first fetch), but at that moment `tracks` is
            empty and no total has arrived — printing "0 tracks" over a list
            that is about to hold thousands states something false.
          -->
          <p
            v-if="!isLoading"
            class="text-muted-foreground shrink-0 text-xs tabular-nums"
            data-testid="track-card-list-count"
          >
            {{ trackCount }} {{ trackCount === 1 ? 'track' : 'tracks' }}
          </p>
        </div>
      </div>

      <div v-if="isLoading" class="text-muted-foreground text-sm">Loading tracks…</div>
      <p
        v-else-if="tracks.length === 0"
        class="text-muted-foreground py-6 text-center text-sm"
      >
        {{ emptyText ?? 'No tracks for this list.' }}
      </p>
      <div v-else class="grid grid-cols-1 gap-3">
        <TrackCard
          v-for="(track, index) in visibleTracks"
          :key="trackKey(track, index)"
          :track="track"
          :density="density"
        />
        <!-- Reaching here mounts the next chunk of cards. -->
        <div
          v-if="hasUnrenderedTracks"
          ref="sentinelRef"
          class="h-px"
          aria-hidden="true"
          data-testid="track-card-list-sentinel"
        />
      </div>
    </template>
  </div>
</template>
